import React, { useEffect, useRef } from 'react'
import { ChevronDown, ChevronUp, X } from 'lucide-react'

export type FindDialogMode = 'find' | 'replace'
export type FindDialogScope = 'sheet' | 'workbook'

interface FindDialogProps {
  mode: FindDialogMode
  query: string
  replacement: string
  scope: FindDialogScope
  canReplace: boolean
  onModeChange: (mode: FindDialogMode) => void
  onQueryChange: (query: string) => void
  onReplacementChange: (value: string) => void
  onScopeChange: (scope: FindDialogScope) => void
  onNext: () => void
  onPrev: () => void
  onReplaceCurrent: () => void
  onReplaceAll: () => void
  onClose: () => void
  matchCount: number
  currentMatchIndex: number
}

export const FindDialog: React.FC<FindDialogProps> = ({
  mode,
  query,
  replacement,
  scope,
  canReplace,
  onModeChange,
  onQueryChange,
  onReplacementChange,
  onScopeChange,
  onNext,
  onPrev,
  onReplaceCurrent,
  onReplaceAll,
  onClose,
  matchCount,
  currentMatchIndex,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const replaceMode = mode === 'replace'
  const currentLabel = matchCount > 0 ? `${currentMatchIndex + 1} of ${matchCount}` : '0 of 0'
  const replaceDisabled = !replaceMode || !canReplace || matchCount <= 0 || !query

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      if (event.shiftKey) onPrev()
      else onNext()
      event.preventDefault()
    } else if (event.key === 'Escape') {
      onClose()
      event.preventDefault()
    }
  }

  return (
    <div className="fg-find-dialog" role="dialog" aria-label={replaceMode ? 'Find and Replace' : 'Find'}>
      <div className="fg-find-dialog-row">
        <button
          type="button"
          className={mode === 'find' ? 'fg-find-mode-button active' : 'fg-find-mode-button'}
          title="Find text in the active sheet (Ctrl+F)"
          aria-pressed={mode === 'find'}
          onClick={() => onModeChange('find')}
        >
          Find
        </button>
        <button
          type="button"
          className={mode === 'replace' ? 'fg-find-mode-button active' : 'fg-find-mode-button'}
          title="Find and replace text in the active sheet (Ctrl+H)"
          aria-pressed={mode === 'replace'}
          onClick={() => onModeChange('replace')}
        >
          Replace
        </button>
        <span className="fg-find-dialog-spacer" />
        <button type="button" className="fg-find-icon-button" onClick={onClose} title="Close (Esc)" aria-label="Close find">
          <X size={14} />
        </button>
      </div>

      <div className="fg-find-dialog-row">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Find in sheet"
          className="fg-find-input"
          title="Find text"
          aria-label="Find text"
        />
        <span className="fg-find-count" title="Current match count">{currentLabel}</span>
        <button type="button" onClick={onPrev} className="fg-find-icon-button" title="Previous match (Shift+Enter)" aria-label="Previous match">
          <ChevronUp size={14} />
        </button>
        <button type="button" onClick={onNext} className="fg-find-icon-button" title="Next match (Enter)" aria-label="Next match">
          <ChevronDown size={14} />
        </button>
      </div>

      {replaceMode && (
        <div className="fg-find-dialog-row">
          <input
            type="text"
            value={replacement}
            onChange={(event) => onReplacementChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Replace with"
            className="fg-find-input"
            title="Replacement text"
            aria-label="Replacement text"
          />
          <button
            type="button"
            className="fg-find-action-button"
            onClick={onReplaceCurrent}
            disabled={replaceDisabled}
            title={canReplace ? 'Replace the current match' : 'Replace requires a live spreadsheet engine sheet'}
            aria-label="Replace current match"
          >
            Replace
          </button>
          <button
            type="button"
            className="fg-find-action-button"
            onClick={onReplaceAll}
            disabled={replaceDisabled}
            title={canReplace ? 'Replace all matches in the active sheet' : 'Replace requires a live spreadsheet engine sheet'}
            aria-label="Replace all matches"
          >
            All
          </button>
        </div>
      )}

      <div className="fg-find-dialog-row">
        <label className="fg-find-scope-label" title="Search scope">
          Scope
          <select
            value={scope}
            onChange={(event) => onScopeChange(event.target.value as FindDialogScope)}
            title="Search scope"
            aria-label="Search scope"
          >
            <option value="sheet">Sheet</option>
            <option value="workbook" disabled>Workbook</option>
          </select>
        </label>
      </div>
    </div>
  )
}
